import { Color3, Constants, MeshBuilder, type AbstractMesh, type Mesh, type Scene, type ShaderMaterial, type Vector3 } from '@babylonjs/core';
import { registerRallyPulseShaders } from './rally-pulse-shaders';
import { createEssenceMist, preloadEssenceMist, type EssenceMist } from './essence-mist';
import type { VfxWarmup } from './vfx-warmup';
import { createVfxShaderMaterial } from './vfx-shader-material';
import { reconcileKeyedEffects } from './keyed-effects';

/** A rally's warm gold-orange: its howl's ring, the mist off those it reached, and their glyph. Apart from the Hasted elite's yellow. */
export const RALLY_COLOR_HEX = '#f29a3b';

/** One body a howl reached, and how strong its flash is now (0 to 1): above 0 once the ring has reached it. */
export interface RallyPulseStruck {
    readonly id: string;
    readonly meshes: readonly AbstractMesh[];
    readonly flash: number;
}

/** A rally's howl going out this frame. */
export interface RallyPulseState {
    readonly id: string;
    /** Where it went out from, on the floor, in scene space. */
    readonly center: Vector3;
    readonly radiusMeters: number;
    /** How far out its ring is (0 to 1); gone at 1. */
    readonly progress: number;
    readonly struck: readonly RallyPulseStruck[];
}

/** The ring's width, its trailing wash, and how far its edges soften, in meters. */
const RING_WIDTH_METERS = 0.07;
const TRAIL_METERS = 0.6;
const FEATHER_METERS = 0.015;
/** It fades over the last of its spread. */
const FADE_FROM_PROGRESS = 0.7;
/** Just above the floor, under ground warnings. */
const FLOOR_OFFSET = 0.025;
/** Motes puffed off a struck body as the ring reaches it, all at once, rather than Instinct's steady stream. */
const PUFF_MOTES = 12;

interface DrawnPulse {
    readonly mesh: Mesh;
    readonly material: ShaderMaterial;
    readonly mists: Map<string, EssenceMist>;
}

/**
 * Draws a rally's howl going out (combat-vfx.md, "Rally"): a thin ring
 * spreading across the floor from where it went out to as far as it
 * carried, gone as it gets there, and a puff of mist rising off each body
 * it reached as the ring passes it. Takes exactly the pulses shown this
 * frame.
 */
export class RallyPulseSystem {
    private readonly drawn = new Map<string, DrawnPulse>();
    private readonly color = Color3.FromHexString(RALLY_COLOR_HEX);

    constructor(private readonly scene: Scene) {
        registerRallyPulseShaders();
        // The puff is brief: its texture must already be in by the first howl.
        preloadEssenceMist(scene);
    }

    warm(warmup: VfxWarmup): void {
        warmup.material(this.createMaterial('rally-pulse-warmup', 1));
    }

    setPulses(states: readonly RallyPulseState[]): void {
        reconcileKeyedEffects(this.drawn, states, {
            create: (state) => this.create(state),
            apply: (pulse, state) => {
                const progress = Math.min(1, Math.max(0, state.progress));
                pulse.mesh.setEnabled(progress < 1);
                pulse.material.setFloat('ringRadius', progress * state.radiusMeters);
                pulse.material.setFloat('opacity', 1 - Math.min(1, Math.max(0, (progress - FADE_FROM_PROGRESS) / (1 - FADE_FROM_PROGRESS))));
                this.flash(state.id, pulse, state.struck);
            },
            unseen: () => 'remove',
            discard: (pulse) => this.disposePulse(pulse),
        });
    }

    dispose(): void {
        this.drawn.forEach((pulse) => this.disposePulse(pulse));
        this.drawn.clear();
    }

    /** Each struck body puffs motes once, as the ring first reaches it; the wisps fade out on their own. */
    private flash(pulseId: string, pulse: DrawnPulse, struck: readonly RallyPulseStruck[]): void {
        for (const body of struck) {
            if (pulse.mists.has(body.id) || body.flash <= 0 || body.meshes.length === 0) {
                continue;
            }
            const mist = createEssenceMist(this.scene, body.meshes, `rally-pulse-${pulseId}-${body.id}`, this.color);
            mist.burst(PUFF_MOTES);
            pulse.mists.set(body.id, mist);
        }
    }

    private create(state: RallyPulseState): DrawnPulse {
        const quadSize = (state.radiusMeters + RING_WIDTH_METERS + FEATHER_METERS * 2) * 2;
        const mesh = MeshBuilder.CreateGround(`rally-pulse-${state.id}`, { width: quadSize, height: quadSize }, this.scene);
        mesh.position.set(state.center.x, state.center.y + FLOOR_OFFSET, state.center.z);
        mesh.isPickable = false;
        const material = this.createMaterial(`rally-pulse-${state.id}`, quadSize);
        mesh.material = material;
        return { mesh, material, mists: new Map() };
    }

    private createMaterial(name: string, quadSize: number): ShaderMaterial {
        const material = createVfxShaderMaterial(this.scene, name, 'rallyPulse', {
            uniforms: ['worldViewProjection', 'quadSize', 'ringRadius', 'ringWidth', 'trailLength', 'feather', 'opacity', 'pulseColor'],
            alphaMode: Constants.ALPHA_ADD,
        });
        material.setFloat('quadSize', quadSize);
        material.setFloat('ringRadius', 0);
        material.setFloat('ringWidth', RING_WIDTH_METERS);
        material.setFloat('trailLength', TRAIL_METERS);
        material.setFloat('feather', FEATHER_METERS);
        material.setFloat('opacity', 1);
        material.setColor3('pulseColor', this.color);
        return material;
    }

    private disposePulse(pulse: DrawnPulse): void {
        pulse.mists.forEach((mist) => mist.dispose());
        pulse.material.dispose();
        pulse.mesh.dispose();
    }
}
